/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import { type Writable, writable, get } from 'svelte/store';

type OverpanelKey =
  | 'search'
  | 'location'
  | 'availableLocation'
  | 'drug'
  | 'drugForm'
  | 'drugSelection'
  | 'drugStrength'
  | 'drugQuantity'
  | 'drugCustomQuantity'
  | 'emailCard'
  | 'textCard'
  | 'textApp'
  | 'imageDetails'
  | 'feedback'
  | 'helpFeedback'
  | 'compare';

const overpanelKeys: OverpanelKey[] = [
  'search',
  'location',
  'availableLocation',
  'drug',
  'drugForm',
  'drugSelection',
  'drugStrength',
  'drugQuantity',
  'drugCustomQuantity',
  'emailCard',
  'textCard',
  'textApp',
  'imageDetails',
  'feedback',
  'helpFeedback',
  'compare'
];

interface OverpanelValue {
  nodeRef: HTMLElement | null;
  useChevronIcon?: boolean;
  closeCallback?: Function;
}

interface OverpanelOptions {
  useChevronIcon?: boolean;
  closeCallback?: Function;
  [key: string]: Function | boolean | string | undefined;
}

type OverpanelMap = Map<OverpanelKey, OverpanelValue>;

export const overpanelMap: Writable<OverpanelMap> = writable(new Map());
export const currentOverpanel: Writable<OverpanelKey | ''> = writable('');

export const focusLastOverpanel = (): void => {
  const overpanels: OverpanelMap = get(overpanelMap);
  const keys = overpanels.keys();
  const lastOverpanelKey = Array.from(keys)[overpanels.size - 1];
  const lastOverpanel = overpanels.get(lastOverpanelKey);

  if (lastOverpanel && lastOverpanel.nodeRef) {
    lastOverpanel.nodeRef.focus();
  }
};

export const openOverpanel = (key: OverpanelKey, options: OverpanelOptions = {}): void => {
  if (!overpanelKeys.includes(key)) return;

  if (options.useChevronIcon === undefined) {
    options.useChevronIcon = Array.from(get(overpanelMap).keys()).length > 0;
  }
  overpanelMap.update((overpanels) => {
    if (!overpanels.has(key)) {
      overpanels.set(key, { ...options, nodeRef: null });
    }
    return overpanels;
  });
};

export const closeOverpanel = (key: OverpanelKey): void => {
  if (!overpanelKeys.includes(key)) return;

  overpanelMap.update((overpanels) => {
    overpanels.delete(key);
    return overpanels;
  });
  focusLastOverpanel();
};

export const setOverpanelNodeRef = (key: OverpanelKey, nodeRef: HTMLElement): void => {
  overpanelMap.update((overpanels) => {
    const currentOverpanel = overpanels.get(key);
    overpanels.set(key, {
      ...currentOverpanel,
      nodeRef,
    });
    return overpanels;
  });
};

export const isCurrentOverpanel = (key: OverpanelKey): boolean => {
  const overpanels: OverpanelMap = get(overpanelMap);
  const keys = overpanels.keys();
  return Array.from(keys)[overpanels.size - 1] === key;
};

export const getOverpanelIndex = (key: OverpanelKey): number => {
  const overpanels: OverpanelMap = get(overpanelMap);
  const keys = overpanels.keys();
  return Array.from(keys).indexOf(key);
};

export const isOpen = (key: OverpanelKey): boolean => {
  return get(overpanelMap).has(key);
};

export const toggleOverpanel = (key: OverpanelKey): void => {
  if (isOpen(key)) {
    closeOverpanel(key);
  } else {
    openOverpanel(key);
  }
};

export const resetOverpanels = (): void => {
  overpanelMap.update((overpanels) => {
    overpanels.clear();
    return overpanels;
  });
};

export const getOverpanelMap = (): OverpanelMap | null => get(overpanelMap);
